<template>
  <v-container fluid class="pa-6">
    <div class="d-flex align-center mb-6">
      <h1 class="text-h4 font-weight-bold">Тарифы</h1>
      <v-spacer></v-spacer>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openDialog()">Создать тариф</v-btn>
    </div>

    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-4">{{ error }}</v-alert>

    <v-card>
      <v-card-text class="pa-0">
        <v-data-table :headers="headers" :items="items" :loading="isLoading" item-value="id" hover
                      :items-per-page="-1" hide-default-footer>
          <template #[`item.name`]="{ item }">
            {{ item.name }}
            <v-chip v-if="item.is_default" size="x-small" color="primary" variant="tonal" class="ml-2">по умолчанию</v-chip>
          </template>
          <template #[`item.findings_per_month`]="{ item }">{{ limitText(item.findings_per_month) }}</template>
          <template #[`item.max_active_projects`]="{ item }">{{ limitText(item.max_active_projects) }}</template>
          <template #[`item.max_user_sources`]="{ item }">{{ limitText(item.max_user_sources) }}</template>
          <template #[`item.actions`]="{ item }">
            <v-btn icon="mdi-pencil" variant="text" size="small" @click="openDialog(item)"></v-btn>
            <v-btn icon="mdi-delete" variant="text" size="small" color="error" :disabled="item.is_default"
                   title="Пользователи тарифа перейдут на тариф по умолчанию" @click="remove(item)"></v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <div class="text-caption text-disabled mt-2">
      Пустое поле — без ограничения. Пользователь без тарифа получает тариф по умолчанию; у администраторов ограничений нет.
      Найденные сообщения считаются за календарный месяц, удалённые не считаются.
    </div>

    <v-dialog v-model="dialog" max-width="520px">
      <v-card>
        <v-card-title>{{ form.id ? 'Тариф' : 'Новый тариф' }}</v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-text-field v-model="form.name" label="Название" variant="outlined" density="compact"
                          :rules="[v => !!(v && v.trim()) || 'Заполните поле']" class="mb-2"></v-text-field>
            <v-text-field v-model="form.findings_per_month" label="Найденные сообщения в месяц" type="number" min="0"
                          variant="outlined" density="compact" :rules="numberRules" hint="Пусто — без ограничения"
                          persistent-hint class="mb-2"></v-text-field>
            <v-text-field v-model="form.max_active_projects" label="Запущенные проекты" type="number" min="0"
                          variant="outlined" density="compact" :rules="numberRules" class="mb-2"></v-text-field>
            <v-text-field v-model="form.max_user_sources" label="«Мои источники»" type="number" min="0"
                          variant="outlined" density="compact" :rules="numberRules" class="mb-2"></v-text-field>
            <v-checkbox v-model="form.is_default" label="Тариф по умолчанию" density="compact" hide-details
                        :disabled="form.wasDefault"></v-checkbox>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="saving" @click="dialog = false">Отменить</v-btn>
          <v-btn color="primary" variant="elevated" :loading="saving" @click="save">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient'

const EMPTY = { id: null, name: '', findings_per_month: '', max_active_projects: '', max_user_sources: '', is_default: false, wasDefault: false }

export default {
  name: 'Plans',
  data() {
    return {
      items: [],
      isLoading: false,
      error: '',
      dialog: false,
      saving: false,
      form: { ...EMPTY },
      numberRules: [v => v === '' || v == null || (Number.isInteger(Number(v)) && Number(v) >= 0) || 'Целое число не меньше 0'],
      headers: [
        { title: 'Тариф', key: 'name', sortable: false },
        { title: 'Найденные сообщения в месяц', key: 'findings_per_month', sortable: false, align: 'end' },
        { title: 'Запущенные проекты', key: 'max_active_projects', sortable: false, align: 'end' },
        { title: '«Мои источники»', key: 'max_user_sources', sortable: false, align: 'end' },
        { title: 'Пользователи', key: 'users_count', sortable: false, align: 'end' },
        { title: '', key: 'actions', sortable: false, width: 110 }
      ]
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    limitText(v) {
      return v == null ? 'без ограничения' : Number(v).toLocaleString('ru-RU')
    },
    async load() {
      this.isLoading = true
      this.error = ''
      try {
        this.items = (await apiClient.get('/admin/plans')).data.items
      } catch (e) {
        this.error = 'Ошибка загрузки тарифов: ' + (e.response?.data?.detail || e.message)
      } finally {
        this.isLoading = false
      }
    },
    openDialog(item) {
      this.form = item
        ? { ...item, wasDefault: item.is_default,
            findings_per_month: item.findings_per_month ?? '', max_active_projects: item.max_active_projects ?? '',
            max_user_sources: item.max_user_sources ?? '' }
        : { ...EMPTY }
      this.dialog = true
    },
    num(v) {
      return v === '' || v == null ? null : Number(v)
    },
    async save() {
      const { valid } = await this.$refs.form.validate()
      if (!valid) return
      const f = this.form
      const body = {
        name: f.name.trim(), is_default: !!f.is_default,
        findings_per_month: this.num(f.findings_per_month),
        max_active_projects: this.num(f.max_active_projects),
        max_user_sources: this.num(f.max_user_sources)
      }
      this.saving = true
      try {
        if (f.id) await apiClient.put(`/admin/plans/${f.id}`, body)
        else await apiClient.post('/admin/plans', body)
        this.dialog = false
        await this.load()
      } catch (e) {
        this.error = 'Не удалось сохранить: ' + (e.response?.data?.detail || e.message)
      } finally {
        this.saving = false
      }
    },
    async remove(item) {
      if (!confirm(`Удалить тариф «${item.name}»? Его пользователи перейдут на тариф по умолчанию.`)) return
      try {
        await apiClient.delete(`/admin/plans/${item.id}`)
        await this.load()
      } catch (e) {
        this.error = 'Не удалось удалить: ' + (e.response?.data?.detail || e.message)
      }
    }
  }
}
</script>
